<template>
  <article
    v-editable="blok"
    :class="{ 'without-page-banner': !pageBanner }"
    class="article-wrapper"
  >
    <div
      v-if="pageBanner"
      :class="{ 'with-page-navigation': shouldShowStickyNavigation }"
      class="page-banner"
    >
      <component
        :is="pageBannerComponent"
        :blok="pageBanner"
        :comments-button="!hideComments"
        :downloads-button="!!props.blok.downloads"
        metabuttons
        :authors="authors"
      />
    </div>
    <section v-if="hasRichTextContent" class="block section-md">
      <div class="container">
        <div class="row flex-wrap justify-around">
          <div v-if="shouldShowStickyNavigation" class="column sm-11 md-5">
            <div class="sticky-sidebar">
              <ArticleStickyNavigation
                :content="richTextContent"
                :introduction="introduction"
                :title="title"
              />
              <ArticleDownloads :downloads="downloads" class="hidden-xs" />
            </div>
          </div>
          <div class="column sm-11 md-7">
            <div class="page-content">
              <RichTextRenderer :document="richTextContent" />
              <ArticleReferences v-if="references" :content="references" />
              <ArticleDownloads
                id="article-downloads"
                :downloads="downloads"
                class="visible-xs"
              />

              <section v-if="authors?.length" class="content">
                <h2>
                  About the Author<span v-if="authors.length > 1">s</span>
                </h2>
                <AuthorCard
                  v-for="author in authors"
                  :key="author.uuid"
                  :blok="author"
                />
              </section>

              <LazyVisibleArticleToolButtons
                :comments-button="!hideComments"
                :introduction="introduction"
                :title="title"
                hydrate-on-visible
              />

              <NewsletterPopupTrigger />
            </div>
          </div>
        </div>
      </div>
    </section>

    <NewsletterPopupTrigger v-if="!hasRichTextContent" />
    <HyvorCommentsForum v-if="!hideComments" :page-id="storyUid" />
    <component
      :is="block.component"
      v-for="block in blocks"
      :key="block._uid"
      :blok="block"
    />

    <!-- Related content block -->
    <BlockRelatedContent v-if="!hideRelated" :blok="blok" />
  </article>
</template>

<script setup lang="ts">
import type { PageStoryblok } from '~/types/storyblok'

import useStoryContent from '~/composables/useStoryContent'

const props = defineProps<{ blok: PageStoryblok }>()
const {
  title,
  storyUid,
  pageBanner,
  pageBannerComponent,
  hasRichTextContent,
  downloads,
  shouldShowStickyNavigation,
  richTextContent,
  blocks,
  introduction,
  references,
  hideComments,
  hideRelated,
  authors,
} = useStoryContent(toRefs(props))
</script>

<style lang="less" scoped>
.page-banner {
  &.with-page-navigation {
    margin-top: @sticky-nav-height;
    @media @tablet {
      margin-top: 0;
    }
  }
}
.article-tool-buttons {
  margin: @gutter-xxl 0 0;
  padding-top: @gutter-lg;
  border-top: solid @2px @color-border;

  :deep(.button-group) {
    .justify-center();
  }
}

.sticky-sidebar {
  position: fixed;
  top: @mobile-nav-height;
  width: 100vw;
  z-index: 10;

  @media @tablet {
    position: sticky;
    margin: 0;
    top: @desktop-nav-primary-height + @desktop-nav-secondary-height;
    padding-bottom: @gutter-xxl * 1.5; // 48 * 1.5 = 72px = share + print buttons
    margin-bottom: @gutter-xxl * 1.5; // 48 * 1.5 = 72px = share + print buttons
    border-bottom: @2px solid @color-fill-divide;
  }

  @media @desktop {
    max-width: @375px;
  }
}
</style>
